import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, Link, List, ListItem, ListItemText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'sticky',
    width: 0,
    top: -48,
    left: 0,
    '& > div': {
      position: 'absolute',
      whiteSpace: 'nowrap',
      top: 80,
      left: 32,
    },
  },
  title: {
    padding: theme.spacing(0, 1),
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  divider: {
    margin: theme.spacing(1, 0, 0.5),
  },
}));

const useListItemStyles = makeStyles()((theme, _params, classes) => ({
  selected: {},
  root: {
    padding: theme.spacing(1.5, 1),
    fontWeight: 500,
    [`&.${classes.selected}`]: {
      fontWeight: 700,
      backgroundColor: theme.palette.custom.gray.extraLight,
    },
    [`&.${classes.selected}:hover, &:hover`]: {
      backgroundColor: theme.palette.custom.gray.extraLight,
    },
  },
}));

const useListItemTextStyles = makeStyles()((theme) => ({
  root: {
    margin: 0,
  },
  primary: {
    color: theme.palette.primary.main,
    fontSize: 13,
    fontWeight: 'inherit',
    lineHeight: 1.2,
  },
}));

const line = 152;

const getHash = (id) => `#${id}`;

function NavigationDrawer({ label, sections = [], className, ...props }) {
  const { classes, cx } = useStyles(props, {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    props,
  });
  const { classes: listItemClasses } = useListItemStyles();
  const { classes: listItemTextClasses } = useListItemTextStyles();
  const [currentHash, setCurrentHash] = useState('');

  const handleScroll = useCallback(() => {
    sections.some(({ id }) => {
      const hash = getHash(id);
      if (currentHash === hash) {
        return false;
      }

      const anchor = document.getElementById(id);
      let match = false;
      if (anchor) {
        const { top = 0, bottom = 0 } = anchor.getBoundingClientRect();

        match = line > top && line < bottom;
        if (match) {
          setCurrentHash(hash);
        }
      }
      return match;
    });
  }, [sections, currentHash]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setCurrentHash(window.location.hash);
    const id = window.location.hash.substring(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className={cx(classes.root, className)}>
      <div>
        <Typography className={classes.title} color="textSecondary" variant="body2">
          {label}
        </Typography>
        <Divider className={classes.divider} light />
        <List>
          {sections.map(({ id, text }) => {
            const hash = getHash(id);
            const selected = hash === currentHash;

            return (
              <ListItem
                classes={listItemClasses}
                button
                key={id}
                selected={selected}
                href={hash}
                component={Link}
                underline="none"
              >
                <ListItemText classes={listItemTextClasses} primary={text} />
              </ListItem>
            );
          })}
        </List>
      </div>
    </div>
  );
}

NavigationDrawer.propTypes = {
  label: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  className: PropTypes.string,
};

export default memo(NavigationDrawer);
